/**
 * WebRTC Screen-Share + Remote Control service.
 *
 * Architecture:
 *   Admin (viewer)  ←── WebRTC video stream ──  User (sharer)
 *   Admin (viewer)  ── control events via DataChannel ──→  User (sharer)
 *
 * Signaling goes through the backend WebSocket at /api/ws/screen-share/{sessionId}
 */

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/**
 * Create a signaling WebSocket connection.
 */
export function createSignalingSocket(sessionId, role, joinCode) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  // The server checks credentials rather than trusting the role: the admin side sends the admin's
  // login token, the sharing side sends the session code.
  const cred = role === 'admin'
    ? `token=${encodeURIComponent(localStorage.getItem('token') || '')}`
    : `code=${encodeURIComponent(joinCode || '')}`;
  const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/ws/screen-share/${sessionId}?role=${role}&${cred}`;
  return new WebSocket(wsUrl);
}

/**
 * ScreenSharePeer — manages the WebRTC connection for one side.
 */
export class ScreenSharePeer {
  constructor({ sessionId, role, joinCode, onRemoteStream, onControlEvent, onStatus, onCursorMove }) {
    this.sessionId = sessionId;
    this.role = role; // "admin" or "user"
    this.joinCode = joinCode;
    this.onRemoteStream = onRemoteStream || (() => {});
    this.onControlEvent = onControlEvent || (() => {});
    this.onStatus = onStatus || (() => {});
    this.onCursorMove = onCursorMove || (() => {});
    this.pc = null;
    this.ws = null;
    this.dataChannel = null;
    this.localStream = null;
    this._keepAlive = null;
  }

  /** Connect to signaling and set up WebRTC. */
  async connect() {
    this.ws = createSignalingSocket(this.sessionId, this.role, this.joinCode);
    this.onStatus('connecting');

    this.ws.onopen = () => {
      this.onStatus('waiting');
      // Keep-alive every 20s
      this._keepAlive = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 20000);
    };

    this.ws.onmessage = async (evt) => {
      const msg = JSON.parse(evt.data);
      switch (msg.type) {
        case 'peer-joined':
          this.onStatus('peer-joined');
          // If we're the user (sharer), create offer when admin joins
          if (this.role === 'user' && msg.role === 'admin') {
            await this._createOffer();
          }
          break;
        case 'offer':
          await this._handleOffer(msg);
          break;
        case 'answer':
          await this._handleAnswer(msg);
          break;
        case 'ice-candidate':
          await this._handleIceCandidate(msg);
          break;
        case 'control-event':
          this.onControlEvent(msg.event);
          break;
        case 'cursor-move':
          this.onCursorMove(msg);
          break;
        case 'peer-left':
          this.onStatus('peer-left');
          break;
        case 'pong':
          break;
        default:
          break;
      }
    };

    this.ws.onclose = () => {
      this.onStatus('disconnected');
      clearInterval(this._keepAlive);
    };

    this.ws.onerror = () => {
      this.onStatus('error');
    };
  }

  /** User side: start sharing screen. */
  async startScreenShare() {
    try {
      this.localStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      // When user stops sharing via browser UI
      this.localStream.getVideoTracks()[0].onended = () => {
        this.onStatus('share-stopped');
      };
      this.onStatus('sharing');
      return this.localStream;
    } catch (err) {
      this.onStatus('share-denied');
      throw err;
    }
  }

  /** Send a control event (mouse/key) from admin to user via DataChannel or WS fallback. */
  sendControlEvent(event) {
    const msg = { type: 'control-event', event };
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify(msg));
    } else if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  /** Send cursor position from admin. */
  sendCursorMove(x, y) {
    const msg = { type: 'cursor-move', x, y };
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  /** Clean up everything. */
  disconnect() {
    clearInterval(this._keepAlive);
    this.localStream?.getTracks().forEach(t => t.stop());
    this.dataChannel?.close();
    this.pc?.close();
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.close();
    }
    this.pc = null;
    this.ws = null;
    this.dataChannel = null;
    this.localStream = null;
  }

  // ── Private: WebRTC negotiation ──────────────────────────────────────

  _createPeerConnection() {
    if (this.pc) return;
    this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    this.pc.onicecandidate = (evt) => {
      if (evt.candidate && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: evt.candidate.toJSON(),
        }));
      }
    };

    this.pc.ontrack = (evt) => {
      this.onRemoteStream(evt.streams[0]);
    };

    this.pc.onconnectionstatechange = () => {
      if (this.pc.connectionState === 'connected') {
        this.onStatus('connected');
      } else if (this.pc.connectionState === 'failed') {
        this.onStatus('connection-failed');
      }
    };

    // DataChannel for low-latency control events
    if (this.role === 'user') {
      // User creates the data channel (as offer creator)
      this.dataChannel = this.pc.createDataChannel('control', { ordered: true });
      this._setupDataChannel(this.dataChannel);
    } else {
      this.pc.ondatachannel = (evt) => {
        this.dataChannel = evt.channel;
        this._setupDataChannel(this.dataChannel);
      };
    }
  }

  _setupDataChannel(dc) {
    dc.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'control-event') {
          this.onControlEvent(msg.event);
        }
      } catch { /* ignore */ }
    };
  }

  async _createOffer() {
    this._createPeerConnection();

    // Add screen-share tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        this.pc.addTrack(track, this.localStream);
      });
    }

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.ws.send(JSON.stringify({
      type: 'offer',
      sdp: offer.sdp,
    }));
  }

  async _handleOffer(msg) {
    this._createPeerConnection();
    await this.pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: msg.sdp }));

    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    this.ws.send(JSON.stringify({
      type: 'answer',
      sdp: answer.sdp,
    }));
  }

  async _handleAnswer(msg) {
    if (this.pc) {
      await this.pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: msg.sdp }));
    }
  }

  async _handleIceCandidate(msg) {
    if (this.pc && msg.candidate) {
      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
      } catch { /* ignore late candidates */ }
    }
  }
}
